import {
  Button,
  Divider,
  FormControl,
  FormLabel,
  Heading,
  Select,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from "@chakra-ui/react";
import { ReactElement, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import api from "../../api";
import { Court } from "../../api/types";
import useFetchCourts from "../../hooks/api/useFetchCourts";
import useFetchHours from "../../hooks/api/useFetchHours";
import useInitSettings from "../../hooks/useInitSettings";
import useShowToast from "../../hooks/useShowToast";
import useSettingsStore from "../../store/settings";
import SettingsCourtsModal from "./settings-courts-modal";

type CourtsSettingsFormData = {
  night_hour_id: number;
  heating_hour_id: number;
};

const SettingsCourts = (): ReactElement => {
  const { t } = useTranslation();
  const { register, handleSubmit, reset } = useForm<CourtsSettingsFormData>();
  const { courts, refetchCourts } = useFetchCourts();
  const { hours } = useFetchHours();
  const { settings } = useSettingsStore();
  const { refreshSettings } = useInitSettings(false);
  const { successToast, errorToast } = useShowToast();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCourt, setSelectedCourt] = useState<Court | undefined>(undefined);

  const {
    isOpen: isCourtsSettingsModalOpen,
    onOpen: onCourtsSettingsModalModalOpen,
    onClose: onCourtsSettingsModalClose,
  } = useDisclosure();

  const openModal = (court?: Court) => {
    if (court) {
      setSelectedCourt(court);
    }
    onCourtsSettingsModalModalOpen();
  };

  const onModalClose = () => {
    setSelectedCourt(undefined);
    refetchCourts();
    onCourtsSettingsModalClose();
  };

  const onSubmit = async (data: CourtsSettingsFormData) => {
    setIsLoading(true);
    const settings = await api.updateSettings({ ...data });
    if (!settings || api.error) {
      console.log("Error updating settings", api.error);
      setIsLoading(false);
      errorToast({
        title: t("settings.general.error_title"),
        description: t("settings.general.error_message.default"),
      });
      return;
    }

    refreshSettings();
    setIsLoading(false);
    successToast({
      title: t("settings.general.success_message"),
      description: t("settings.general.success_message"),
    });
  };

  useEffect(() => {
    if (!settings) {
      return;
    }

    reset({
      night_hour_id: settings.night_hour_id,
      heating_hour_id: settings.heating_hour_id,
    });
  }, [settings, hours, reset]);

  return (
    <>
      <Heading size="lg" mb={4}>
        {t("settings.courts.title")}
      </Heading>

      <TableContainer>
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>{t("settings.courts.name")}</Th>
              <Th>{t("settings.courts.indoor")}</Th>
              <Th> </Th>
            </Tr>
          </Thead>
          <Tbody>
            {courts.map(court => (
              <Tr key={court.id}>
                <Td>{court.name}</Td>
                <Td>{court.is_indoor ? "Sì" : "No"}</Td>
                <Td>
                  <Button colorScheme="blue" size="xs" onClick={() => openModal(court)}>
                    {t("settings.courts.edit_court")}
                  </Button>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>

      <Button mt={4} colorScheme="blue" onClick={() => openModal()}>
        {t("settings.courts.add_court")}
      </Button>

      <Divider my={6} />

      <form onSubmit={handleSubmit(onSubmit)}>
        <FormControl mb={4}>
          <FormLabel>{t("settings.courts.night_hour")}</FormLabel>
          <Select {...register("night_hour_id", { valueAsNumber: true })}>
            {hours.map(hour => (
              <option key={hour.id} value={hour.id}>
                {hour.label}
              </option>
            ))}
          </Select>
        </FormControl>
        <FormControl mb={4}>
          <FormLabel>{t("settings.courts.heating_hour")}</FormLabel>
          <Select {...register("heating_hour_id", { valueAsNumber: true })}>
            {hours.map(hour => (
              <option key={hour.id} value={hour.id}>
                {hour.label}
              </option>
            ))}
          </Select>
        </FormControl>

        <Button mt={6} colorScheme="blue" type="submit" isLoading={isLoading}>
          {t("settings.courts.save")}
        </Button>
      </form>

      <SettingsCourtsModal
        isOpen={isCourtsSettingsModalOpen}
        onClose={onModalClose}
        court={selectedCourt}
      />
    </>
  );
};

export default SettingsCourts;
